/**
 * Image delivery through Netlify Image CDN.
 *
 * The project photography is a mix of 960px and 2148px JPEGs on S3, served
 * with no CDN, no modern formats and no responsive sizes — so a phone
 * downloads the same multi-hundred-kilobyte file a desktop does, from a single
 * region in London.
 *
 * Netlify Image CDN fixes all three at the edge without moving any files:
 *
 *   /.netlify/images?url=<source>&w=<width>&fm=avif&q=<quality>
 *
 * Local paths under public/ work out of the box; the S3 bucket is allow-listed
 * in netlify.toml.
 *
 * Two safety rails, because this endpoint only exists on Netlify:
 *
 *  1. It is bypassed outside production builds, so `npm start` and any other
 *     host keep loading the original URLs untouched.
 *  2. It can be switched off entirely with REACT_APP_DISABLE_IMAGE_CDN=true,
 *     without a code change, if anything misbehaves after a deploy.
 *
 * `SmartImage` additionally falls back to the original URL at runtime if a
 * transformed URL fails to load, so a misconfiguration degrades to today's
 * behaviour rather than to broken images.
 */

/** Widths offered in srcset. Chosen around the site's real layout columns. */
export const RESPONSIVE_WIDTHS = [480, 768, 1024, 1440, 1920, 2560];

/** Width requested for the plain `src` fallback (non-srcset browsers). */
const FALLBACK_WIDTH = 1440;

/*
  AVIF quality.

  This was 72, which produced 125-170 KB per cover. On a 390px phone at device
  pixel ratio 3 the browser needs 1170px, so it picks the 1440w candidate —
  eight of those came to just over 1 MB on the home page, and the archive felt
  slow to scroll on a real phone.

  Measured on an actual cover at w=1440:
    q72 -> 169 KB    q60 -> 115 KB    q50 -> 79 KB    q45 -> 65 KB

  q50 was compared against q72 at 2x zoom on a detailed facade (timber slats,
  glazing reflections, pool mosaic) and is indistinguishable, so it halves the
  bytes for nothing. q45 was left on the table deliberately: the extra 14 KB
  is not worth reducing the margin on a portfolio where image quality is the
  product.
*/
const DEFAULT_QUALITY = 50;

/**
 * Whether transformed URLs should be emitted at all.
 *
 * @returns {boolean}
 */
export function isImageCdnEnabled() {
  if (process.env.REACT_APP_DISABLE_IMAGE_CDN === "true") return false;
  return process.env.NODE_ENV === "production";
}

/**
 * Build a single transformed image URL.
 *
 * @param {string} sourceUrl        Original absolute or root-relative URL.
 * @param {Object} [options]
 * @param {number} [options.width]  Target width in pixels.
 * @param {string} [options.format="avif"] Output format.
 * @param {number} [options.quality]
 * @returns {string} The transformed URL, or `sourceUrl` unchanged when the
 *   CDN is not in use.
 */
export function buildImageUrl(sourceUrl, options = {}) {
  if (!sourceUrl || !isImageCdnEnabled()) return sourceUrl;

  const {
    width = FALLBACK_WIDTH,
    format = "avif",
    quality = DEFAULT_QUALITY,
  } = options;

  const parameters = new URLSearchParams({
    url: sourceUrl,
    w: String(width),
    fm: format,
    q: String(quality),
  });

  return `/.netlify/images?${parameters.toString()}`;
}

/**
 * Build a `srcset` covering RESPONSIVE_WIDTHS.
 *
 * Widths larger than the source are skipped where the source width is known,
 * so the CDN is never asked to upscale.
 *
 * @param {string} sourceUrl
 * @param {Object} [options]
 * @param {number} [options.maxWidth] Intrinsic width of the source, if known.
 * @param {string} [options.format]
 * @param {number} [options.quality]
 * @returns {string | undefined} undefined when the CDN is not in use.
 */
export function buildSrcSet(sourceUrl, options = {}) {
  if (!sourceUrl || !isImageCdnEnabled()) return undefined;

  const { maxWidth, format, quality } = options;

  const usableWidths = RESPONSIVE_WIDTHS.filter((candidateWidth) => {
    if (!maxWidth) return true;
    // Keep the first width at or above the source so the largest layout slot
    // is still served something sharp, then stop.
    return candidateWidth <= maxWidth * 1.05;
  });

  const widths = usableWidths.length > 0 ? usableWidths : [RESPONSIVE_WIDTHS[0]];

  return widths
    .map(
      (candidateWidth) =>
        `${buildImageUrl(sourceUrl, {
          width: candidateWidth,
          format,
          quality,
        })} ${candidateWidth}w`
    )
    .join(", ");
}
